import { OperatorOption } from '../constants';
import type { FilterInput } from '../constants';
import { isAnyEbvField, isEbvPercentileField } from './ebvFilterFields';
import {
  buildPercentilePairFilters,
  expandEbvCompositeForQuery,
} from './ebvPercentileFilterHelpers';
import { parseRangeCompositeValue } from './rangeValue';
import {
  expandWildcardPattern,
  hasWildcardChars,
  expandAnimalIdentChips,
  isIdentChipQuickSearchField,
  isIdentChipStyleFilter,
} from './animalIdentChipFilter';

/** Flat comparison row before society-specific field name mapping (e.g. ebv1 → EBV1). */
export type FilterComparisonRow = {
  field: string;
  operator: string;
  value: string;
};

/**
 * Expands filter rows that are not a single (field, operator, value) for GraphQL:
 * - EBV composite value (no operator) → one or more comparisons
 * - Wildcard pattern (no operator) → one or more comparisons
 * - BETWEEN → percentile rank pair semantics, or plain GTE/LTE for other fields
 *
 * Returns null when the caller should apply its generic path for this row.
 */
export function tryExpandFilterInputToComparisonRows(
  filter: FilterInput,
): FilterComparisonRow[] | null {
  const { field, operator, value } = filter;
  if (!field) return null;

  const valueStr = typeof value === 'string' ? value : String(value ?? '');

  if (isAnyEbvField(field) && !operator && valueStr) {
    const composite = parseRangeCompositeValue(valueStr);
    return expandEbvCompositeForQuery(field, composite).map((p) => ({
      field,
      operator: p.operator,
      value: p.value,
    }));
  }

  if (!operator && hasWildcardChars(valueStr)) {
    return expandWildcardPattern(valueStr).map((p) => ({
      field,
      operator: p.operator,
      value: p.value,
    }));
  }

  if (operator === OperatorOption.between.value) {
    const parsedRange = parseRangeCompositeValue(valueStr);
    if (isEbvPercentileField(field)) {
      return buildPercentilePairFilters(
        parsedRange?.min?.trim() ?? '',
        parsedRange?.max?.trim() ?? '',
      ).map((p) => ({
        field,
        operator: p.operator,
        value: p.value,
      }));
    }

    const out: FilterComparisonRow[] = [];
    if (parsedRange?.min?.trim()) {
      out.push({
        field,
        operator: OperatorOption.greaterThanOrEqual.value,
        value: parsedRange.min.trim(),
      });
    }
    if (parsedRange?.max?.trim()) {
      out.push({
        field,
        operator: OperatorOption.lessThanOrEqual.value,
        value: parsedRange.max.trim(),
      });
    }
    return out;
  }

  return null;
}

/**
 * True when the row still needs user input before running search / preview count.
 * Matches {@link tryExpandFilterInputToComparisonRows}: EBV composite rows use
 * field + value only (no operator).
 */
export function isFilterInputIncompleteForQuery(filter: FilterInput): boolean {
  const { field, operator, value } = filter;
  if (!field) return true;

  const valueStr = typeof value === 'string' ? value : String(value ?? '');

  if (isAnyEbvField(field) && !operator) {
    return !valueStr.trim();
  }

  // Ident chip rows (animal, sire/dam, breeder/owner, member) store the
  // operator per-chip via wildcards rather than in the row's `operator`
  // field — complete when at least one non-empty chip is present.
  if (
    isIdentChipQuickSearchField(field) &&
    isIdentChipStyleFilter(operator, valueStr)
  ) {
    return expandAnimalIdentChips(valueStr).length === 0;
  }

  // Hero quick-search fields such as sale name also store wildcard patterns
  // without a row operator. The query builder derives the comparisons from
  // the pattern when it contains at least one searchable segment.
  if (!operator && hasWildcardChars(valueStr)) {
    return tryExpandFilterInputToComparisonRows(filter)?.length === 0;
  }

  if (!operator) return true;

  if (operator === OperatorOption.isNotEmpty.value) return false;

  if (operator === OperatorOption.between.value) {
    const parsedRange = parseRangeCompositeValue(valueStr);
    return !(parsedRange?.min?.trim() || parsedRange?.max?.trim());
  }

  return !valueStr.trim();
}
